import '@/styles/globals.css';
import '@/styles/responsive.css';
import '@rainbow-me/rainbowkit/styles.css';
import type { AppProps } from 'next/app';
import { WagmiProvider } from 'wagmi';
import { Toaster } from '@/components/ui/sonner';
import { RainbowKitProvider } from '@rainbow-me/rainbowkit';
import { AnimatePresence } from 'framer-motion';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { ConfigurationProvider } from '@/contexts/ConfigurationProvider';
import { LitSessionProvider } from '@/contexts/LitSessionProvider';
import { ThemeProvider } from '@/contexts/ThemeProvider';
import dynamic from 'next/dynamic';
import { SwapProvider } from '@/contexts/SwapProvider';
import { SnipeProvider } from '@/contexts/SnipeProvider';
import { SessionProvider } from 'next-auth/react';
import Script from 'next/script';
import ErrorBoundary from '@/components/shared/ErrorBoundary';
import { wagmiConfig } from '@/config/wagmiConfig';
const MoonPayProvider = dynamic(() => import('@moonpay/moonpay-react').then(mod => mod.MoonPayProvider), {
  ssr: false
});
const queryClient = new QueryClient();
export default function App({
  Component,
  pageProps: {
    session,
    ...pageProps
  }
}: AppProps) {
  return <ErrorBoundary data-sentry-element="ErrorBoundary" data-sentry-component="App" data-sentry-source-file="_app.tsx">
      <WagmiProvider config={wagmiConfig} data-sentry-element="WagmiProvider" data-sentry-source-file="_app.tsx">
        <QueryClientProvider client={queryClient} data-sentry-element="QueryClientProvider" data-sentry-source-file="_app.tsx">
          <RainbowKitProvider data-sentry-element="RainbowKitProvider" data-sentry-source-file="_app.tsx">
            <MoonPayProvider apiKey={process.env.NEXT_PUBLIC_MOONPAY_PUBLIC_KEY} debug data-sentry-element="MoonPayProvider" data-sentry-source-file="_app.tsx">
              <LitSessionProvider data-sentry-element="LitSessionProvider" data-sentry-source-file="_app.tsx">
                <SwapProvider data-sentry-element="SwapProvider" data-sentry-source-file="_app.tsx">
                  <SnipeProvider data-sentry-element="SnipeProvider" data-sentry-source-file="_app.tsx">
                    <ConfigurationProvider data-sentry-element="ConfigurationProvider" data-sentry-source-file="_app.tsx">
                      <AnimatePresence initial={false} mode='popLayout' data-sentry-element="AnimatePresence" data-sentry-source-file="_app.tsx">
                        <ThemeProvider attribute='class' defaultTheme='dark' enableSystem={false} data-sentry-element="ThemeProvider" data-sentry-source-file="_app.tsx">
                          <SessionProvider data-sentry-element="SessionProvider" data-sentry-source-file="_app.tsx">
                            <div className='lg:hidden'>
                              <Component {...pageProps} data-sentry-element="Component" data-sentry-source-file="_app.tsx" />
                            </div>
                            <div className='hidden lg:flex items-center h-screen desktopBanner w-full'>
                              <div className='w-1/2'></div>
                              <div className='w-1/2 pr-8'>
                                <h1 className='text-genius-gradient text-[50px] tracking-tighter pb-8	'>
                                  Desktop is landing soon
                                </h1>
                                <div className='text-[20px] font-light text-genius-gradient-normal tracking-tighter	 ta-l justify-start w-[402px]'>
                                  <p>
                                    For now, Genius is mobile-first. We will be releasing a desktop
                                    version in mid November.{' '}
                                  </p>
                                </div>
                                <div className='justify-start ta-l'>
                                  <img className='ml-[-70px] mt-[-30px]' src='/static/images/landing-qr.png' width={303} height={303} alt='qr-code' />
                                </div>
                              </div>
                            </div>
                            {/* Google Analytics */}
                            <meta name='viewport' content='width=device-width, initial-scale=1, maximum-scale=1' data-sentry-element="meta" data-sentry-source-file="_app.tsx" />
                            <Script src='https://www.googletagmanager.com/gtag/js?id=G-QTG76SZSVV' strategy='afterInteractive' data-sentry-element="Script" data-sentry-source-file="_app.tsx" />
                            <Script id='google-analytics' strategy='afterInteractive' data-sentry-element="Script" data-sentry-source-file="_app.tsx">
                              {`

          window.dataLayer = window.dataLayer || [];
          function gtag(){dataLayer.push(arguments);}
          gtag('js', new Date());

          gtag('config', 'G-QTG76SZSVV');
        `}
                            </Script>
                          </SessionProvider>
                          <Toaster duration={5000} closeButton={true} data-sentry-element="Toaster" data-sentry-source-file="_app.tsx" />
                        </ThemeProvider>
                      </AnimatePresence>
                    </ConfigurationProvider>
                  </SnipeProvider>
                </SwapProvider>
              </LitSessionProvider>
              <ReactQueryDevtools data-sentry-element="ReactQueryDevtools" data-sentry-source-file="_app.tsx" />
            </MoonPayProvider>
          </RainbowKitProvider>
        </QueryClientProvider>
      </WagmiProvider>
    </ErrorBoundary>;
}